import { useMutation, useQuery } from "@tanstack/react-query";
import { get, getBlob } from "./divertidogsApiUtils";
import { RespuestaBlob } from "../utils/blobUtils";

export interface IngresoPorMes {
  anoMes: string;
  ingresos: number;
}

export interface DescuentoPorMes {
  anoMes: string;
  descuentos: number;
}

export interface IngresoPorDia {
  fecha: string;
  ingresos: number;
}

export interface IngresoPorPropietario {
  idPropietario: string;
  propietario: string;
  ingresos: number;
}

export interface IngresoPorMascota {
  idMascota: string;
  nombre: string;
  propietario: string;
  ingresos: number;
}

export interface IngresoPorServicio {
  idServicio: string;
  nombre: string;
  tipoServicio: string;
  ingresos: number;
}

interface ParametrosDescargaIngresosPorMes {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaDescuentosPorMes {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaIngresosPorDia {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaIngresosPorPropietario {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaIngresosPorMascota {
  fechaInicio: string;
  fechaFin: string;
}

interface ParametrosDescargaIngresosPorServicio {
  fechaInicio: string;
  fechaFin: string;
}

export const obtenerIngresosPorMes = async (
  fechaInicio: string,
  fechaFin: string
): Promise<IngresoPorMes[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<IngresoPorMes[]>(
    `analisis/finanzas/ingresos-por-mes?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const obtenerDescuentosPorMes = async (
  fechaInicio: string,
  fechaFin: string
): Promise<DescuentoPorMes[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<DescuentoPorMes[]>(
    `analisis/finanzas/descuentos-por-mes?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const QUERY_KEY_INGRESOS_POR_MES = "QUERY_KEY_INGRESOS_POR_MES";

export const QUERY_KEY_DESCUENTOS_POR_MES = "QUERY_KEY_DESCUENTOS_POR_MES";

export const QUERY_KEY_INGRESOS_POR_DIA = "QUERY_KEY_INGRESOS_POR_DIA";

export const QUERY_KEY_INGRESOS_POR_PROPIETARIO =
  "QUERY_KEY_INGRESOS_POR_PROPIETARIO";

export const QUERY_KEY_INGRESOS_POR_MASCOTA = "QUERY_KEY_INGRESOS_POR_MASCOTA";

export const QUERY_KEY_INGRESOS_POR_SERVICIO =
  "QUERY_KEY_INGRESOS_POR_SERVICIO";

export const useObtenerIngresosPorMes = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_INGRESOS_POR_MES, fechaInicio, fechaFin],
    async () => await obtenerIngresosPorMes(fechaInicio, fechaFin)
  );
};

export const useObtenerDescuentosPorMes = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_DESCUENTOS_POR_MES, fechaInicio, fechaFin],
    async () => await obtenerDescuentosPorMes(fechaInicio, fechaFin)
  );
};

export const obtenerIngresosPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<IngresoPorDia[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<IngresoPorDia[]>(
    `analisis/finanzas/ingresos-por-dia?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerIngresosPorDia = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_INGRESOS_POR_DIA, fechaInicio, fechaFin],
    async () => await obtenerIngresosPorDia(fechaInicio, fechaFin)
  );
};

export const obtenerIngresosPorPropietario = async (
  fechaInicio: string,
  fechaFin: string
): Promise<IngresoPorPropietario[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<IngresoPorPropietario[]>(
    `analisis/finanzas/ingresos-por-propietario?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerIngresosPorPropietario = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_INGRESOS_POR_PROPIETARIO, fechaInicio, fechaFin],
    async () => await obtenerIngresosPorPropietario(fechaInicio, fechaFin)
  );
};

export const obtenerIngresosPorMascota = async (
  fechaInicio: string,
  fechaFin: string
): Promise<IngresoPorMascota[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<IngresoPorMascota[]>(
    `analisis/finanzas/ingresos-por-mascota?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerIngresosPorMascota = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_INGRESOS_POR_MASCOTA, fechaInicio, fechaFin],
    async () => await obtenerIngresosPorMascota(fechaInicio, fechaFin)
  );
};

export const obtenerIngresosPorServicio = async (
  fechaInicio: string,
  fechaFin: string
): Promise<IngresoPorServicio[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<IngresoPorServicio[]>(
    `analisis/finanzas/ingresos-por-servicio?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerIngresosPorServicio = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_INGRESOS_POR_SERVICIO, fechaInicio, fechaFin],
    async () => await obtenerIngresosPorServicio(fechaInicio, fechaFin)
  );
};

export const descargarIngresosPorDia = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/ingresos-por-dia/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarIngresosPorDia = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaIngresosPorDia) =>
      await descargarIngresosPorDia(fechaInicio, fechaFin)
  );

export const descargarIngresosPorPropietario = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/ingresos-por-propietario/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarIngresosPorPropietario = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaIngresosPorPropietario) =>
      await descargarIngresosPorPropietario(fechaInicio, fechaFin)
  );

export const descargarIngresosPorMascota = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/ingresos-por-mascota/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarIngresosPorMascota = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaIngresosPorMascota) =>
      await descargarIngresosPorMascota(fechaInicio, fechaFin)
  );

export const descargarIngresosPorServicio = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/ingresos-por-servicio/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarIngresosPorServicio = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaIngresosPorServicio) =>
      await descargarIngresosPorServicio(fechaInicio, fechaFin)
  );

export const descargarIngresosPorMes = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/ingresos-por-mes/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarIngresosPorMes = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaIngresosPorMes) =>
      await descargarIngresosPorMes(fechaInicio, fechaFin)
  );

export const descargarDescuentosPorMes = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/descuentos-por-mes/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarDescuentosPorMes = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaDescuentosPorMes) =>
      await descargarDescuentosPorMes(fechaInicio, fechaFin)
  );

export interface DeudaPorPropietario {
  idPropietario: string;
  propietario: string;
  documento: string;
  totalPendiente: number;
}

interface ParametrosDescargaDeudasPorPropietario {
  fechaInicio: string;
  fechaFin: string;
}

export const QUERY_KEY_DEUDAS_POR_PROPIETARIO =
  "QUERY_KEY_DEUDAS_POR_PROPIETARIO";

export const obtenerDeudasPorPropietario = async (
  fechaInicio: string,
  fechaFin: string
): Promise<DeudaPorPropietario[] | undefined> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return get<DeudaPorPropietario[]>(
    `analisis/finanzas/deudas-por-propietario?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useObtenerDeudasPorPropietario = (
  fechaInicio: string,
  fechaFin: string
) => {
  return useQuery(
    [QUERY_KEY_DEUDAS_POR_PROPIETARIO, fechaInicio, fechaFin],
    async () => await obtenerDeudasPorPropietario(fechaInicio, fechaFin)
  );
};

export const descargarDeudasPorPropietario = async (
  fechaInicio: string,
  fechaFin: string
): Promise<RespuestaBlob> => {
  const queryFechaInicio = encodeURIComponent(fechaInicio);
  const queryFechaFin = encodeURIComponent(fechaFin);
  return getBlob(
    `analisis/finanzas/deudas-por-propietario/descargar?fechaInicio=${queryFechaInicio}&fechaFin=${queryFechaFin}`
  );
};

export const useDescargarDeudasPorPropietario = () =>
  useMutation(
    async ({ fechaInicio, fechaFin }: ParametrosDescargaDeudasPorPropietario) =>
      await descargarDeudasPorPropietario(fechaInicio, fechaFin)
  );